<script lang="ts" setup>
import { computed, reactive, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAxios } from '@/composables/useAxios'
import { handleAxiosError, getErrorMessage } from '@/composables/useAxiosError'
import { useNotifyStore } from '@/stores'
import { uuid } from '@/composables'
import { STATUS_CODES } from '@/constants'
import { formatCurrency } from '@/utils/formatCurrency'
import { AccountSplit, type DryRunResult } from '@/types'

const props = defineProps<{
  show: boolean
  splitId: number | null
  dryRunResult: DryRunResult | null
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'executed'): void
}>()

const { t } = useI18n()
const router = useRouter()
const notifyStore = useNotifyStore()

const state = reactive({
  loading: false,
  error: null as string | null
})

watch(
  () => props.show,
  (val) => {
    if (val) {
      state.error = null
      state.loading = false
    }
  }
)

const close = () => {
  if (state.loading) return
  emit('close')
}

const formatCents = (cents: number, currency?: string): string => {
  return formatCurrency(cents / 100, (currency || 'EUR').toUpperCase(), 'de-DE')
}

// safeResult re-validates the incoming dry-run payload once per prop change,
// instead of on every reactive read in the template.
const safeResult = computed<DryRunResult | null>(() => {
  if (!props.dryRunResult) return null
  const r = AccountSplit.shape.dry_run_result.safeParse(props.dryRunResult)
  return r.success ? (r.data ?? null) : props.dryRunResult
})

const vatWarnings = computed<string[]>(() => {
  const r = safeResult.value
  if (!r) return []
  return (r.warnings || []).filter((w) => /vat|mwst|ust/i.test(w))
})

const confirmExecute = async () => {
  if (!props.splitId) return
  state.loading = true
  state.error = null

  try {
    const res = await useAxios<null, { status: string }>(
      'POST',
      `/admin/account-splits/${props.splitId}/execute`,
      true
    )
    // Execute runs synchronously; 200 means completed or partial_failure, 202 means still running.
    if (res && (res.status === STATUS_CODES.OK || res.status === STATUS_CODES.ACCEPTED)) {
      const finalStatus = res.data?.status
      const isPartial = finalStatus === 'partial_failure'
      notifyStore.createNotify({
        id: uuid('v4'),
        alertType: isPartial ? 'warning' : 'success',
        title: t('common.success'),
        message: isPartial
          ? t('dashboard.admin.accountSplit.notify.executePartial')
          : finalStatus === 'completed'
            ? t('dashboard.admin.accountSplit.notify.executeCompleted')
            : t('dashboard.admin.accountSplit.notify.executeStarted')
      })
      emit('executed')
      emit('close')
      router.push({ name: 'dashboardAdminAccountSplitDetail', params: { id: props.splitId } })
    }
  } catch (error) {
    handleAxiosError(error as Error, { logPrefix: 'AccountSplitDryRunDialog:execute' })
    state.error = getErrorMessage(error as Error)
    notifyStore.createNotify({
      id: uuid('v4'),
      alertType: 'error',
      title: t('common.error'),
      message: state.error
    })
  } finally {
    state.loading = false
  }
}
</script>

<template>
  <v-dialog :model-value="show" max-width="780" persistent @update:model-value="close">
    <div class="modal">
      <div class="modal-header">
        <v-icon icon="mdi-clipboard-text-search-outline" size="22" class="header-icon" />
        <h3 class="modal-title">{{ $t('dashboard.admin.accountSplit.dryRun.title') }}</h3>
      </div>

      <div class="modal-content">
        <template v-if="safeResult">
          <!-- Parties summary -->
          <div class="parties">
            <div class="party">
              <span class="party-label">{{ $t('dashboard.admin.accountSplit.detail.sourceAccount') }}</span>
              <span class="party-id">#{{ safeResult!.source.account_id }}</span>
              <span class="party-stripe mono">{{ safeResult!.source.stripe_customer_id || '—' }}</span>
            </div>
            <div class="party">
              <span class="party-label">{{ $t('dashboard.admin.accountSplit.detail.targetAccount') }}</span>
              <span class="party-id">#{{ safeResult!.target.account_id }}</span>
              <span class="party-stripe mono">{{ safeResult!.target.stripe_customer_id || '—' }}</span>
            </div>
          </div>

          <!-- VAT warning info-box -->
          <div v-if="vatWarnings.length > 0" class="info-box info-box--warning">
            <v-icon icon="mdi-alert-circle-outline" size="18" />
            <div class="info-box-content">
              <p class="info-box-title">{{ $t('dashboard.admin.accountSplit.dryRun.vatDelta') }}</p>
              <ul class="info-box-list">
                <li v-for="(w, i) in vatWarnings" :key="i">{{ w }}</li>
              </ul>
            </div>
          </div>

          <!-- Planned actions -->
          <div class="subsection">
            <h4 class="subsection-title">{{ $t('dashboard.admin.accountSplit.dryRun.plannedActions') }}</h4>
            <div v-if="safeResult!.items.length === 0" class="state-box">
              <p class="empty-hint">{{ $t('dashboard.admin.accountSplit.dryRun.noItems') }}</p>
            </div>
            <div v-else class="items-list">
              <div v-for="it in safeResult!.items" :key="it.item_id" class="kv-row">
                <div class="kv-label">
                  <span class="asset-type">{{ $t(`dashboard.admin.accountSplit.assetType.${it.asset_type}`) }}</span>
                  <span class="asset-id mono">#{{ it.asset_id }}</span>
                </div>
                <div class="kv-value">
                  <ul class="planned-actions">
                    <li v-for="(a, ai) in it.planned_actions" :key="ai">{{ a }}</li>
                  </ul>
                  <div v-if="it.warnings && it.warnings.length > 0" class="item-warnings">
                    <span v-for="(w, wi) in it.warnings" :key="wi" class="status-chip status-warning">{{ w }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Open invoices -->
          <div class="subsection">
            <h4 class="subsection-title">{{ $t('dashboard.admin.accountSplit.dryRun.openInvoices') }}</h4>
            <div v-if="safeResult!.open_invoices.length === 0" class="state-box">
              <p class="empty-hint">{{ $t('dashboard.admin.accountSplit.dryRun.noChanges') }}</p>
            </div>
            <div v-else class="data-table">
              <div class="table-header invoices-cols">
                <span class="col">Invoice</span>
                <span class="col">{{ $t('dashboard.admin.accountSplit.dryRun.beforeTotal') }}</span>
                <span class="col">{{ $t('dashboard.admin.accountSplit.dryRun.afterTotal') }}</span>
              </div>
              <div
                v-for="inv in safeResult!.open_invoices"
                :key="inv.invoice_id"
                class="table-row invoices-cols"
              >
                <span class="col mono">{{ inv.invoice_id }}</span>
                <span class="col mono">{{ formatCents(inv.current_total_cents, inv.currency) }}</span>
                <span class="col after-split">
                  <span v-for="(p, i) in inv.after_split || []" :key="i" class="after-split-row">
                    <span class="mono customer">{{ p.customer }}</span>
                    <span class="mono">{{ formatCents(p.total_cents, inv.currency) }}</span>
                  </span>
                </span>
              </div>
            </div>
          </div>

          <!-- Warnings -->
          <div class="subsection">
            <h4 class="subsection-title">{{ $t('dashboard.admin.accountSplit.dryRun.warnings') }}</h4>
            <div v-if="safeResult!.warnings.length === 0" class="state-box">
              <p class="empty-hint">{{ $t('dashboard.admin.accountSplit.dryRun.noWarnings') }}</p>
            </div>
            <ul v-else class="warning-list">
              <li v-for="(w, i) in safeResult!.warnings" :key="i">{{ w }}</li>
            </ul>
          </div>
        </template>

        <div v-else class="state-box">
          <v-icon icon="mdi-information-outline" size="32" class="empty-icon" />
          <p class="empty-title">{{ $t('dashboard.admin.accountSplit.dryRun.title') }}</p>
        </div>

        <p v-if="state.error" class="dialog-error">{{ state.error }}</p>
      </div>

      <div class="modal-footer">
        <button class="btn-text" :disabled="state.loading" @click="close">
          {{ $t('dashboard.admin.accountSplit.actions.cancel') }}
        </button>
        <button
          class="btn-create"
          :disabled="state.loading || !splitId || !safeResult"
          @click="confirmExecute"
        >
          <v-progress-circular
            v-if="state.loading"
            indeterminate
            size="16"
            width="2"
            color="white"
          />
          <template v-else>
            <v-icon icon="mdi-rocket-launch-outline" size="16" />
            <span>{{ $t('dashboard.admin.accountSplit.actions.confirmExecute') }}</span>
          </template>
        </button>
      </div>
    </div>
  </v-dialog>
</template>

<style lang="scss" scoped>
.modal {
  background: rgba(15, 23, 48, 0.98);
  border: 1px solid rgba(0, 176, 255, 0.2);
  border-radius: 16px;
  overflow: hidden;
}

.modal-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 20px 24px;
  border-bottom: 1px solid rgba(120, 163, 255, 0.1);

  .header-icon {
    color: #00B0FF;
  }
}

.modal-title {
  font-size: 1.05rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.95);
  margin: 0;
}

.modal-content {
  padding: 20px 24px;
  max-height: 70vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 14px 24px;
  border-top: 1px solid rgba(120, 163, 255, 0.1);
}

.btn-create {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: linear-gradient(135deg, #00B0FF, #1FE2D4);
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;

  &:hover:not(:disabled) {
    box-shadow: 0 4px 12px rgba(0, 176, 255, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.btn-text {
  padding: 8px 14px;
  background: rgba(120, 163, 255, 0.08);
  border: 1px solid rgba(120, 163, 255, 0.15);
  border-radius: 8px;
  color: rgba(200, 218, 255, 0.8);
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;

  &:hover:not(:disabled) {
    background: rgba(120, 163, 255, 0.15);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.parties {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
}

.party {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(120, 163, 255, 0.08);
  border-radius: 10px;
  padding: 12px 14px;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.party-label {
  font-size: 0.72rem;
  font-weight: 600;
  color: rgba(200, 218, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.party-id {
  font-size: 0.95rem;
  color: rgba(255, 255, 255, 0.92);
  font-weight: 600;
}

.party-stripe {
  font-size: 0.78rem;
  color: rgba(200, 218, 255, 0.7);
}

.info-box {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: rgba(0, 176, 255, 0.04);
  border: 1px solid rgba(0, 176, 255, 0.15);
  border-radius: 10px;
  padding: 14px;

  .v-icon {
    color: #4fc3f7;
    margin-top: 2px;
  }

  &--warning {
    background: rgba(255, 183, 77, 0.08);
    border-color: rgba(255, 183, 77, 0.3);
    .v-icon {
      color: #ffb74d;
    }
  }
}

.info-box-content {
  flex: 1;
}

.info-box-title {
  font-weight: 600;
  font-size: 0.88rem;
  color: rgba(255, 255, 255, 0.9);
  margin: 0 0 4px;
}

.info-box-list {
  margin: 0;
  padding-left: 18px;
  color: rgba(220, 232, 255, 0.85);
  font-size: 0.85rem;

  li {
    line-height: 1.5;
  }
}

.subsection-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.9);
  margin: 0 0 8px;
}

.items-list {
  display: flex;
  flex-direction: column;
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(120, 163, 255, 0.08);
  border-radius: 10px;
  overflow: hidden;
}

.kv-row {
  display: grid;
  grid-template-columns: 220px 1fr;
  gap: 12px;
  padding: 12px 14px;
  border-bottom: 1px solid rgba(120, 163, 255, 0.05);

  &:last-child {
    border-bottom: none;
  }
}

.kv-label {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.asset-type {
  font-weight: 600;
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.9);
}

.asset-id {
  font-size: 0.78rem;
  color: rgba(200, 218, 255, 0.6);
}

.kv-value {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.planned-actions {
  margin: 0;
  padding-left: 18px;
  font-size: 0.85rem;
  color: rgba(220, 232, 255, 0.85);

  li {
    line-height: 1.45;
  }
}

.item-warnings {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.status-chip {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 0.76rem;
  font-weight: 500;
}

.status-warning {
  background: rgba(255, 183, 77, 0.1);
  border: 1px solid rgba(255, 183, 77, 0.3);
  color: #ffb74d;
}

.data-table {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(120, 163, 255, 0.08);
  border-radius: 10px;
  overflow-x: auto;
}

.invoices-cols {
  display: grid;
  grid-template-columns: minmax(0, 1.4fr) minmax(0, 1fr) minmax(0, 1.6fr);
  gap: 8px;
  align-items: flex-start;
  padding: 10px 12px;
}

.table-header {
  border-bottom: 1px solid rgba(120, 163, 255, 0.08);
  font-size: 0.72rem;
  font-weight: 600;
  color: rgba(200, 218, 255, 0.5);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.table-row {
  border-bottom: 1px solid rgba(120, 163, 255, 0.05);
  font-size: 0.85rem;
  color: rgba(255, 255, 255, 0.85);

  &:last-child {
    border-bottom: none;
  }
}

.after-split {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.after-split-row {
  display: flex;
  justify-content: space-between;
  gap: 8px;
  font-size: 0.82rem;
}

.customer {
  color: rgba(200, 218, 255, 0.7);
}

.mono {
  font-family: 'JetBrains Mono', 'Fira Code', monospace;
}

.state-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 6px;
  padding: 20px;
  text-align: center;
}

.empty-icon {
  color: rgba(200, 218, 255, 0.25);
}

.empty-title {
  font-size: 0.95rem;
  font-weight: 500;
  color: rgba(200, 218, 255, 0.6);
  margin: 0;
}

.empty-hint {
  font-size: 0.85rem;
  color: rgba(200, 218, 255, 0.5);
  margin: 0;
}

.warning-list {
  margin: 0;
  padding-left: 18px;
  font-size: 0.85rem;
  color: rgba(220, 232, 255, 0.85);

  li {
    line-height: 1.5;
  }
}

.dialog-error {
  font-size: 0.85rem;
  color: #ef5350;
  background: rgba(244, 67, 54, 0.08);
  border: 1px solid rgba(244, 67, 54, 0.2);
  border-radius: 8px;
  padding: 10px 12px;
  margin: 0;
}

@media (max-width: 640px) {
  .parties {
    grid-template-columns: 1fr;
  }
  .kv-row {
    grid-template-columns: 1fr;
  }
  .invoices-cols {
    grid-template-columns: 1fr;
  }
}
</style>
